const MOUNT_ID   = 'nda-particles-root';
const LETTERS     = ['N', 'D', 'A'] as const;
const PER_LETTER  = 400;
const TOTAL       = PER_LETTER * LETTERS.length;

interface Preset {
  letterW: number; letterH: number;
  gap:      number;
  noiseAmp: number;
  zDepth:   number;
  rotAmp:   number;
  ptSize:   number;
  fovDist:  number;
}

const PRESETS: Record<'dark' | 'light', Preset> = {
  dark: {
    letterW: 130, letterH: 260,
    gap:      22,
    noiseAmp: 3.35,
    zDepth:   29,
    rotAmp:   0.00,
    ptSize:   1.1,
    fovDist:  680,
  },
  light: {
    letterW: 130, letterH: 260,
    gap:      22,
    noiseAmp: 2.60,
    zDepth:   24,
    rotAmp:   0.00,
    ptSize:   0.9,
    fovDist:  600,
  },
};

function init() {
  const mountEl = document.getElementById(MOUNT_ID);
  if (!mountEl) return;
  const mount = mountEl as HTMLElement;

  // Resolve mode from data attribute or default to dark
  const rawMode = mount.dataset.mode ?? 'dark';
  const mode: 'dark' | 'light' = rawMode === 'light' ? 'light' : 'dark';
  const P = PRESETS[mode];

  // ── Canvas setup ─────────────────────────────────────────────────────────────

  const canvas = document.createElement('canvas');
  canvas.style.cssText = 'display:block;width:100%;height:100%;';
  mount.appendChild(canvas);
  const ctx = canvas.getContext('2d')!;

  let W = 0, H = 0;

  function resize() {
    W = mount.offsetWidth;
    H = mount.offsetHeight;
    canvas.width  = W;
    canvas.height = H;
  }

  const ro = new ResizeObserver(resize);
  ro.observe(mount);
  resize();

  // ── Letter sampling ───────────────────────────────────────────────────────────

  const rnd = (a: number, b: number) => a + Math.random() * (b - a);

  function sampleLetter(char: string, count: number): [number, number][] {
    const sw = 300, sh = 400;
    const off = document.createElement('canvas');
    off.width = sw; off.height = sh;
    const oc = off.getContext('2d')!;
    oc.fillStyle = '#fff';
    oc.fillRect(0, 0, sw, sh);
    oc.fillStyle = '#000';
    oc.font = `900 ${Math.round(sh * 0.88)}px "Arial Black", Arial, sans-serif`;
    oc.textAlign    = 'center';
    oc.textBaseline = 'middle';
    oc.fillText(char, sw / 2, sh / 2);
    const data = oc.getImageData(0, 0, sw, sh).data;
    const hits: [number, number][] = [];
    for (let y = 0; y < sh; y++)
      for (let x = 0; x < sw; x++)
        if (data[(y * sw + x) * 4] < 110)
          hits.push([x / (sw - 1), y / (sh - 1)]);
    const step = Math.max(1, Math.floor(hits.length / count));
    const pts: [number, number][] = [];
    for (let i = 0; i < hits.length && pts.length < count; i += step)
      pts.push(hits[i]);
    while (pts.length < count) pts.push(pts[pts.length - 1]);
    return pts;
  }

  const letterNorm = LETTERS.map(ch => sampleLetter(ch, PER_LETTER));

  interface NoiseParams { phase: number; freq: number; zSeed: number; }
  const noise: NoiseParams[] = Array.from({ length: TOTAL }, () => ({
    phase: rnd(0, Math.PI * 2),
    freq:  rnd(0.4, 1.2),
    zSeed: (Math.random() - 0.5) * 2,
  }));

  // ── Projection ────────────────────────────────────────────────────────────────

  interface Projected { sx: number; sy: number; sc: number; }

  function project(wx: number, wy: number, wz: number, rotY: number, rotX: number): Projected {
    const cosY = Math.cos(rotY), sinY = Math.sin(rotY);
    const rx  =  wx * cosY - wz * sinY;
    const rz1 =  wx * sinY + wz * cosY;
    const cosX = Math.cos(rotX), sinX = Math.sin(rotX);
    const ry  =  wy * cosX - rz1 * sinX;
    const rz  =  wy * sinX + rz1 * cosX;
    const sc  = P.fovDist / (P.fovDist + rz);
    return { sx: W / 2 + rx * sc, sy: H / 2 - ry * sc, sc };
  }

  // ── Render loop ───────────────────────────────────────────────────────────────

  const isDark = mode === 'dark';

  function frame(ts: number) {
    const now  = ts * 0.001;
    const rotY = Math.sin(now * 0.12) * P.rotAmp;
    const rotX = Math.sin(now * 0.08) * 0.04;

    const totalW = 3 * P.letterW + 2 * P.gap;
    const startX = (W - totalW) / 2;
    const startY = (H - P.letterH) / 2;

    ctx.clearRect(0, 0, W, H);
    if (isDark) {
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, W, H);
    }

    const projected: Projected[] = [];
    letterNorm.forEach((pts, li) => {
      const zx = startX + li * (P.letterW + P.gap);
      const zy = startY;
      pts.forEach(([nx, ny], pi) => {
        const idx = li * PER_LETTER + pi;
        const n   = noise[idx];
        const wx  = zx + nx * P.letterW - W / 2
                  + Math.sin(now * n.freq       + n.phase)       * P.noiseAmp;
        const wy  = -(zy + ny * P.letterH - H / 2)
                  + Math.cos(now * n.freq * 1.4 + n.phase + 1.3) * P.noiseAmp;
        const wz  = n.zSeed * P.zDepth
                  + Math.sin(now * n.freq * 0.7 + n.phase + 2.1) * P.noiseAmp * 0.4;
        projected.push(project(wx, wy, wz, rotY, rotX));
      });
    });

    projected.sort((a, b) => a.sc - b.sc);

    projected.forEach(({ sx, sy, sc }) => {
      const r       = Math.max(P.ptSize * sc, 0.4);
      const opacity = (isDark ? 0.35 : 0.30) + 0.65 * sc;
      ctx.beginPath();
      ctx.arc(sx, sy, r, 0, Math.PI * 2);
      ctx.fillStyle = isDark
        ? `rgba(255,255,255,${opacity.toFixed(2)})`
        : `rgba(12,12,12,${opacity.toFixed(2)})`;
      ctx.fill();
    });

    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
